/* ═══════════════════════════════════════════════════
 *  CaseFilePage — full case-detail view
 *  Migrated from FE/Caseview/casefile.js (4,759 lines)
 * ═══════════════════════════════════════════════════ */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faChevronDown,
  faChevronRight,
  faHistory,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';

import {
  useCaseFile,
  useCaseNotes,
  useCaseTasks,
  useSolicitors,
  useDefendants,
  useDefendantSolicitors,
  useCaseStages,
  useStageHistory,
  useUpdateCase,
  useAddNote,
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
  useReassignCase,
  useDraftTemplates,
  useEmailTemplates,
  useSaveRiskAssessment,
  useSavePreIssueChecklist,
  useSearchContacts,
  useDeleteCase,
  usePortalNotes,
  useCreatePortalNote,
  useDeletePortalNote,
  useCaseAuditLog,
  useAddTimeEntry,
} from '../../hooks/useCaseFile';
import { useFieldDefs } from '../../hooks/useCases';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { useTimer } from '../../hooks/useTimerStore';
import api from '../../services/api';
import {
  LAYOUT_PLAN,
  COLLAPSIBLE_CATEGORIES,
  type CaseTask,
  type ContactSearchResult,
} from '../../types/caseFile';
import type { FieldDef } from '../../types/cases';
import { CONFIG } from '../../config';
import { BRAND_NAME } from '../../constants';

import DatePicker from '../../components/ui/DatePicker/DatePicker';
import SearchableSelect from '../../components/ui/SearchableSelect/SearchableSelect';
import {
  NoteModal,
  CreateTaskModal,
  EditTaskModal,
  RecordTimeModal,
  RecordTimeModalV2,
  DraftAssistantModal,
  FieldValidationModal,
  EmailPreviewModal,
  CompleteTaskModal,
  RiskAssessmentModal,
  PreIssueChecklistModal,
  AircallModal,
  DeleteCaseModal,
  StageHistoryModal,
  ReassignModal,
  CaseAuditModal,
  EmailDrafterModal,
  ClientHeaderCard,
  ActionBar,
  TasksTable,
  NotesTable,
  PortalNotes,
} from './components';
import s from './CaseFilePage.module.css';

/* ── Type definitions ── */
type StageGuardError = {
  response?: {
    data?: {
      code?: string;
      details?: {
        guards?: Array<{ message?: string }>;
      };
    };
  };
};

type AxiosResponseHeaders = Record<string, unknown>;

type EmailAttachment = {
  name: string;
  type: string;
  content?: string | Uint8Array | ArrayBuffer;
};

function extractStageGuardError(error: unknown): {
  code?: string;
  guards?: Array<{ message?: string }>;
} {
  const maybeError = error as StageGuardError;
  return {
    code: maybeError.response?.data?.code,
    guards: maybeError.response?.data?.details?.guards,
  };
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  const maybeError = error as { response?: { data?: { error?: string } } };
  return maybeError.response?.data?.error || 'An error occurred';
}

/* ── Helpers ── */
function fmtUKDate(val: unknown): string {
  if (!val) return '—';
  const str = String(val);
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    try {
      return new Date(str).toLocaleDateString('en-GB');
    } catch {
      return str;
    }
  }
  return str;
}

function toInputDate(val: unknown): string {
  if (!val) return '';
  const str = String(val);
  // Plain YYYY-MM-DD (e.g. from value_date EAV column) — return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  // Timestamp string — use UTC methods so the result is identical in every timezone
  // (backend guarantees UTC midnight storage, so UTC date == intended calendar date globally)
  const d = new Date(str);
  if (isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function contactName(c: Record<string, unknown>): string {
  const first = c['First Name'] || c.first_name || '';
  const last = c['Last Name'] || c.last_name || '';
  return `${first} ${last}`.trim() || 'Unknown';
}

/* ── Aircall user mapping ── */
const AIRCALL_USER_MAP = CONFIG.AIRCALL_USER_MAP;

const AIRCALL_API_GATEWAY = CONFIG.API_GATEWAY_AIRCALL;

function getAircallUserId(username: string): number | null {
  const normalized = username.toLowerCase().replace(/\s+/g, '');
  return AIRCALL_USER_MAP[normalized] ?? null;
}

function validatePhoneNumber(phone: string): boolean {
  if (!phone) return false;
  const cleaned = phone.replace(/[\s\-().]/g, '');
  return [/^\+44[1-9]\d{9,10}$/, /^0[1-9]\d{8,9}$/, /^[1-9]\d{9,10}$/].some((p) => p.test(cleaned));
}

function formatPhoneE164(phone: string): string {
  const cleaned = phone.replace(/[\s\-().]/g, '');
  if (cleaned.startsWith('+44')) return cleaned;
  if (cleaned.startsWith('0')) return '+44' + cleaned.substring(1);
  return '+44' + cleaned;
}

/* ═══════════════════════════════════════════════
 *  Main component
 * ═══════════════════════════════════════════════ */
export default function CaseFilePage() {
  const { id: caseRef } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const urlCaseTypeId = searchParams.get('caseTypeId');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const timerStore = useTimer();

  const formatElapsed = (ms: number) => {
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
  };

  const { allowedCaseTypes, isSuperAdmin, hasPermission } = usePermissions();
  const canReassign = isSuperAdmin || hasPermission('reassign_case');
  const canViewAudit = isSuperAdmin || hasPermission('view_case_audit');
  const canEditStage = isSuperAdmin || hasPermission('edit_case_stage');
  const canExportData = isSuperAdmin || hasPermission('export_data');
  const canCreateTask = isSuperAdmin || hasPermission('create_task');
  const canRecordTime = isSuperAdmin || hasPermission('record_time');
  const canDeleteCase = isSuperAdmin || hasPermission('delete_case');
  const canSendEmail = isSuperAdmin || hasPermission('send_client_email');

  /* ── Data queries ── */
  const { data: caseFile, isLoading, error } = useCaseFile(caseRef);
  const { data: notes = [] } = useCaseNotes(caseRef);
  const { data: tasks = [] } = useCaseTasks(caseRef);
  const { data: portalNotesData } = usePortalNotes(caseRef);
  const createPortalNote = useCreatePortalNote(caseRef);
  const deletePortalNote = useDeletePortalNote(caseRef);

  const caseData = caseFile?.case;
  const contact = caseFile?.contact;

  /* ── Resolve case type ID: URL param (fast) → API response (fallback) ── */
  const caseTypeId: number | null = urlCaseTypeId
    ? Number(urlCaseTypeId)
    : (caseData?.case_type_id ?? null);

  /* Display name for the header (still useful for UI, derived from case data) */
  const caseTypeName =
    caseData?.case_type_display ||
    caseData?.case_type_name ||
    caseData?.case_type ||
    caseData?.CaseType ||
    null;

  /* ── Dropdown data ── */
  const { data: solicitors = [] } = useSolicitors();
  const { data: defendants = [] } = useDefendants();
  const { data: defSolicitors = [] } = useDefendantSolicitors();
  const { data: stages = [] } = useCaseStages(caseTypeId);
  const { data: fieldDefs = [] } = useFieldDefs(caseTypeId);

  /* ── Stage history ── */
  const [showStageHistory, setShowStageHistory] = useState(false);
  const [portalNoteText, setPortalNoteText] = useState('');
  const { data: stageHistory = [] } = useStageHistory(caseRef, showStageHistory);

  /* ── Mutations ── */
  const updateCase = useUpdateCase(caseRef);
  const addNote = useAddNote(caseRef);
  const createTask = useCreateTask(caseRef);
  const updateTaskMut = useUpdateTask();
  const deleteTaskMut = useDeleteTask();
  const reassignCase = useReassignCase(caseRef);
  const saveRiskAssessment = useSaveRiskAssessment();
  const savePreIssueChecklist = useSavePreIssueChecklist();
  const searchContacts = useSearchContacts();
  const deleteCaseMut = useDeleteCase(caseRef);
  const addTimeEntry = useAddTimeEntry(caseRef);

  /* ── Delete case state ── */
  const [deleteCaseModalOpen, setDeleteCaseModalOpen] = useState(false);
  const handleDeleteCase = async () => {
    try {
      await deleteCaseMut.mutateAsync();
      showToast('Case deleted successfully', 'success');
      setDeleteCaseModalOpen(false);
      navigate('/cases');
    } catch {
      showToast('Failed to delete case', 'error');
    }
  };

  /* ── Clio sync ── */
  const [isClioSyncing, setIsClioSyncing] = useState(false);
  const handleSyncToClio = async () => {
    if (!caseRef) return;
    setIsClioSyncing(true);
    try {
      const { data } = await api.post('/api/integrations/clio/sync-case', { case_ref: caseRef });
      const r = data?.result;
      if (r?.already_existed) {
        showToast(
          `Case is already in Clio (matter #${r.clio_matter_id}). Please verify in Clio.`,
          'warning',
        );
      } else {
        showToast(
          `Synced to Clio — matter #${r.clio_matter_id} created. ${r.documents_uploaded} doc(s) uploaded${r.documents_failed ? `, ${r.documents_failed} failed` : ''}. ${r.tasks_synced} task(s) synced${r.tasks_failed ? `, ${r.tasks_failed} failed` : ''}.`,
          'success',
        );
      }
      if (r?.clio_matter_id) setField('clio_matter_id', r.clio_matter_id);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Clio sync failed';
      showToast(message, 'error');
    } finally {
      setIsClioSyncing(false);
    }
  };

  const handleForceSyncToClio = async () => {
    if (!caseRef) return;
    setIsClioSyncing(true);
    try {
      const { data } = await api.post('/api/integrations/clio/sync-case', {
        case_ref: caseRef,
        force: true,
      });
      const r = data?.result;
      showToast(
        `Re-synced to Clio — matter #${r.clio_matter_id}. ${r.documents_uploaded} doc(s) uploaded${r.documents_failed ? `, ${r.documents_failed} failed` : ''}. ${r.tasks_synced} task(s) synced${r.tasks_failed ? `, ${r.tasks_failed} failed` : ''}.`,
        'success',
      );
      if (r?.clio_matter_id) setField('clio_matter_id', r.clio_matter_id);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Clio re-sync failed';
      showToast(message, 'error');
    } finally {
      setIsClioSyncing(false);
    }
  };

  /* ── Draft Assistant data ── */
  const { data: draftTemplates = [] } = useDraftTemplates(caseTypeId);
  const { data: emailTemplates = [] } = useEmailTemplates();

  /* ── Form state: tracks edits to case fields ── */
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [dirty, setDirty] = useState(false);

  // Re-seed form when case data loads
  useEffect(() => {
    if (caseData) {
      setFormValues({});
      setDirty(false);
    }
  }, [caseData]);

  // Update browser tab title with caseRef
  useEffect(() => {
    if (caseRef) {
      document.title = `Case File - ${caseRef}`;
    }
    return () => {
      document.title = BRAND_NAME;
    };
  }, [caseRef]);

  /** Get current value of a field (edited value or original) */
  const getFieldValue = useCallback(
    (key: string): unknown => {
      if (key in formValues) return formValues[key];
      // Reconstruct concatenated def_sol_address from individual columns on page load
      if (key === 'def_sol_address' && !caseData?.['def_sol_address']) {
        const parts = [
          caseData?.def_sol_address_line_1,
          caseData?.def_sol_address_line_2,
          caseData?.def_sol_address_line_3,
          caseData?.def_sol_address_postcode,
        ].filter(Boolean);
        if (parts.length > 0) return parts.join(', ');
      }
      return caseData?.[key] ?? '';
    },
    [formValues, caseData],
  );

  /** Set a field value */
  const setField = useCallback((key: string, value: unknown) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  }, []);

  /* ── Collapsible categories ── */
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());
  const toggleCategory = (cat: string) => {
    setExpandedCats((prev) => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };

  /* ── Group fields by category ── */
  const categorised = useMemo(() => {
    const map: Record<string, FieldDef[]> = {};
    for (const f of fieldDefs) {
      const cat = f.category_name || 'General';
      if (!map[cat]) map[cat] = [];
      map[cat].push(f);
    }
    return map;
  }, [fieldDefs]);

  /* ── Save handler ── */
  const handleSave = async () => {
    if (!caseRef || Object.keys(formValues).length === 0) return;
    try {
      await updateCase.mutateAsync(formValues);
      showToast('Case saved successfully', 'success');
      setFormValues({});
      setDirty(false);
    } catch (err: unknown) {
      const { code, guards } = extractStageGuardError(err);
      if (code === 'STAGE_GUARD_BLOCKED' && Array.isArray(guards) && guards.length > 0) {
        const first = guards[0]?.message ?? 'Required field not populated';
        const extra = guards.length > 1 ? ` (+${guards.length - 1} more)` : '';
        showToast(`Stage blocked: ${first}${extra}`, 'error', 10000);
      } else {
        showToast('Failed to save case', 'error');
      }
    }
  };

  /* ── Note modal ── */
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteContact, setNoteContact] = useState({ phone: false, email: false, none: false });

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await addNote.mutateAsync({
        Note: noteText.trim(),
        user: user?.username || 'Unknown',
        phone_contact: noteContact.phone,
        email_contact: noteContact.email,
        no_contact: noteContact.none,
      });
      showToast('Note added', 'success');
      setNoteText('');
      setNoteContact({ phone: false, email: false, none: false });
      setNoteModalOpen(false);
    } catch {
      showToast('Failed to add note', 'error');
    }
  };

  /* ── Task modal ── */
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({
    assigned_to: '',
    description: '',
    deadline: '',
    deadlineHour: '09',
    deadlineMinute: '00',
    allow_reminders: false,
    reminderDate: '',
    reminderHour: '09',
    reminderMinute: '00',
    category: 'misc' as string,
  });

  /* ── Default task to current user when opening ── */
  const openTaskModal = useCallback(() => {
    const currentUserId = user?.sub || user?.username || '';
    setTaskForm({
      assigned_to: currentUserId,
      description: '',
      deadline: '',
      deadlineHour: '09',
      deadlineMinute: '00',
      allow_reminders: false,
      reminderDate: '',
      reminderHour: '09',
      reminderMinute: '00',
      category: 'misc',
    });
    setTaskModalOpen(true);
  }, [user]);

  const handleCreateTask = async () => {
    if (!taskForm.description.trim() || !taskForm.assigned_to) return;
    const deadline = taskForm.deadline
      ? `${taskForm.deadline}T${taskForm.deadlineHour}:${taskForm.deadlineMinute}:00`
      : '';
    try {
      await createTask.mutateAsync({
        assigned_to: taskForm.assigned_to,
        description: taskForm.description.trim(),
        deadline,
        allow_reminders: taskForm.allow_reminders,
        reminder_datetime:
          taskForm.allow_reminders && taskForm.reminderDate
            ? `${taskForm.reminderDate}T${taskForm.reminderHour}:${taskForm.reminderMinute}:00`
            : undefined,
        category: taskForm.category,
      });
      showToast('Task created', 'success');
      setTaskForm({
        assigned_to: '',
        description: '',
        deadline: '',
        deadlineHour: '09',
        deadlineMinute: '00',
        allow_reminders: false,
        reminderDate: '',
        reminderHour: '09',
        reminderMinute: '00',
        category: 'misc',
      });
      setTaskModalOpen(false);
    } catch {
      showToast('Failed to create task', 'error');
    }
  };

  /* ── Edit task modal ── */
  const [editTask, setEditTask] = useState<CaseTask | null>(null);
  const [editTaskForm, setEditTaskForm] = useState({ description: '', deadline: '', status: '' });

  const openEditTask = (task: CaseTask) => {
    setEditTask(task);
    setEditTaskForm({
      description: task.Description,
      deadline: toInputDate(task.Deadline) ? `${toInputDate(task.Deadline)}T09:00` : '',
      status: task.Status,
    });
  };

  const handleUpdateTask = async () => {
    if (!editTask) return;
    try {
      await updateTaskMut.mutateAsync({
        TaskID: editTask.TaskID,
        Description: editTaskForm.description,
        Deadline: editTaskForm.deadline,
        Status: editTaskForm.status,
      });
      showToast('Task updated', 'success');
      setEditTask(null);
    } catch {
      showToast('Failed to update task', 'error');
    }
  };

  const handleDeleteTask = async () => {
    if (!editTask) return;
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await deleteTaskMut.mutateAsync(editTask.TaskID);
      showToast('Task deleted', 'success');
      setEditTask(null);
    } catch {
      showToast('Failed to delete task', 'error');
    }
  };

  /* ── Record Time ── */
  const [timeModalOpen, setTimeModalOpen] = useState(false);
  const [timeTaskDesc, setTimeTaskDesc] = useState('');
  const [timeManualUnits, setTimeManualUnits] = useState('');
  // V2 fields (only used when TR_BILLING_V2_FORM_ENABLED)
  const [timeActivityCodeId, setTimeActivityCodeId] = useState('');
  const [timeBillable, setTimeBillable] = useState(false);

  const resetTimeForm = () => {
    setTimeTaskDesc('');
    setTimeManualUnits('');
    setTimeActivityCodeId('');
    setTimeBillable(false);
  };

  const handleStartTimer = () => {
    if (!caseRef) return;
    const started = timerStore.startTimer({
      caseRef,
      taskDescription: timeTaskDesc,
      manualUnits: timeManualUnits,
      activityCodeId: timeActivityCodeId || null,
      billable: timeBillable,
    });
    if (!started) {
      showToast('A timer is already running — stop it before starting a new one', 'error');
      return;
    }
    setTimeModalOpen(false);
    resetTimeForm();
    showToast('Timer started', 'success');
  };

  const handleSaveEntry = async () => {
    if (!caseRef) return;
    const units = parseInt(timeManualUnits, 10);
    const now = new Date();
    try {
      if (CONFIG.TR_BILLING_V2_FORM_ENABLED) {
        await api.post('/api/time-entries', {
          caseRef,
          userId: user?.sub ?? user?.username ?? 'unknown',
          startedAt: now.toISOString(),
          endedAt: now.toISOString(),
          durationSeconds: 0, // manual entry: no clock ran; units is the billing metric
          units,
          taskDescription: timeTaskDesc,
          activityCodeId: timeActivityCodeId || null,
          billable: timeBillable,
          source: 'manual',
        });
      } else {
        await addTimeEntry.mutateAsync({
          Duration: `${units * 6} min`,
          StartTime: now.toISOString(),
          EndTime: now.toISOString(),
          Task: timeTaskDesc,
          Units: units,
          User: user?.username || 'System',
        });
      }
      showToast('Time entry recorded', 'success');
    } catch {
      showToast('Failed to record time entry', 'error');
    }
    setTimeModalOpen(false);
    resetTimeForm();
  };

  /* ── Draft Assistant ── */
  const [draftModalOpen, setDraftModalOpen] = useState(false);
  const [draftTab, setDraftTab] = useState<'letter' | 'email'>('letter');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedEmailTemplateId, setSelectedEmailTemplateId] = useState('');
  const [draftGenerating, setDraftGenerating] = useState(false);

  // Field validation modal state
  type MissingField = {
    fieldName: string;
    displayName: string;
    type: string;
    dbTable?: string;
    dbColumn?: string;
    editable?: boolean;
    required?: boolean;
    options?: { value: string; label: string }[] | null;
    isManualInput?: boolean;
    canSave?: boolean;
    dbFieldKey?: string | null;
    sourceTable?: string | null;
    defaultValue?: string;
  };
  type ScanData = {
    templateName: string;
    fileType: string;
    templateFile?: string;
    fieldData: Record<string, string>;
    missingFields: MissingField[];
    totalFields: number;
  };
  const [fieldValidationOpen, setFieldValidationOpen] = useState(false);
  const [fieldValidationPhase, setFieldValidationPhase] = useState<
    'scanning' | 'ready' | 'missing'
  >('scanning');
  const [scanProgress, setScanProgress] = useState(0);
  const [scanData, setScanData] = useState<ScanData | null>(null);
  const [missingFieldValues, setMissingFieldValues] = useState<Record<string, string>>({});
  const [fieldSaving, setFieldSaving] = useState(false);

  // Kick off the scan → show field validation modal
  const handleGenerateLetter = async () => {
    if (!selectedTemplateId || !caseRef) return;
    setDraftGenerating(true);

    // Reset field validation
    setMissingFieldValues({});
    setScanData(null);
    setFieldValidationPhase('scanning');
    setScanProgress(0);
    setFieldValidationOpen(true);

    try {
      const isDat = selectedTemplateId.startsWith('dat_');
      setScanProgress(30);

      if (isDat) {
        const datId = selectedTemplateId.replace('dat_', '');
        const scanRes = await api.post(`/api/documents/dat-scan/${datId}`, { caseRef });
        const raw = scanRes.data;
        setScanProgress(80);
        const missing: MissingField[] = (raw.missing_fields ?? raw.missingFields ?? []).map(
          (f: Record<string, unknown>) => ({
            fieldName: (f.fieldName || f.name || f.field_name || '') as string,
            displayName: (f.displayName || f.display_name || f.fieldName || '') as string,
            type: (f.type || 'text') as string,
            dbTable: f.dbTable as string | undefined,
            dbColumn: f.dbColumn as string | undefined,
            editable: f.editable !== false,
            options: (f.options as { value: string; label: string }[] | null | undefined) ?? null,
            isManualInput: f.isManualInput === true,
            canSave: f.canSave !== false,
            dbFieldKey: (f.dbFieldKey as string | null | undefined) ?? null,
            sourceTable: (f.sourceTable as string | null | undefined) ?? null,
            defaultValue: (f.defaultValue as string | undefined) ?? '',
          }),
        );
        const sd: ScanData = {
          templateName: `dat_${datId}`,
          fileType: 'dat',
          fieldData: raw.fieldData ?? raw.field_data ?? {},
          missingFields: missing,
          totalFields: raw.totalFields ?? raw.total_fields ?? 0,
        };
        setScanData(sd);
        setScanProgress(100);
        if (missing.length === 0) {
          // No missing fields — generate immediately
          await generateDocumentWithScan(sd, {});
        } else {
          setFieldValidationPhase('missing');
          setDraftGenerating(false);
        }
      } else {
        // Legacy template flow
        const scanRes = await api.post('/api/documents/scan', {
          caseRef,
          templateName: selectedTemplateId,
        });
        const raw = scanRes.data;
        setScanProgress(80);
        const missing: MissingField[] = (raw.missingFields ?? raw.missing_fields ?? []).map(
          (f: Record<string, unknown>) => ({
            fieldName: (f.fieldName || '') as string,
            displayName: (f.displayName || f.fieldName || '') as string,
            type: (f.type || 'text') as string,
            dbTable: f.dbTable as string | undefined,
            dbColumn: f.dbColumn as string | undefined,
            editable: f.editable !== false,
            required: f.required as boolean | undefined,
            options: (f.options as { value: string; label: string }[] | null | undefined) ?? null,
            isManualInput: f.isManualInput === true,
            canSave: f.canSave !== false,
            dbFieldKey: (f.dbFieldKey as string | null | undefined) ?? null,
            sourceTable: (f.sourceTable as string | null | undefined) ?? null,
            defaultValue: (f.defaultValue as string | undefined) ?? '',
          }),
        );
        const sd: ScanData = {
          templateName: raw.templateName ?? selectedTemplateId,
          fileType: raw.fileType ?? 'docx',
          templateFile: raw.templateFile,
          fieldData: raw.fieldData ?? {},
          missingFields: missing,
          totalFields: raw.totalFields ?? 0,
        };
        setScanData(sd);
        setScanProgress(100);
        if (missing.length === 0) {
          setFieldValidationPhase('ready');
          await generateDocumentWithScan(sd, {});
        } else {
          setFieldValidationPhase('missing');
          setDraftGenerating(false);
        }
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to scan template';
      showToast(msg, 'error');
      setFieldValidationOpen(false);
      setDraftGenerating(false);
    }
  };

  // Actually generate the document after scan (or after user fills missing fields)
  const generateDocumentWithScan = async (sd: ScanData, overrides: Record<string, string>) => {
    setDraftGenerating(true);
    setFieldValidationPhase('scanning');
    setScanProgress(50);
    try {
      const isDat = sd.templateName.startsWith('dat_');
      if (isDat) {
        const datId = sd.templateName.replace('dat_', '');
        const genRes = await api.post(`/api/documents/dat-generate/${datId}`, {
          caseRef,
          manual_values: overrides,
          fieldOverrides: overrides,
        });
        const result = genRes.data;
        if (result.success && result.url) {
          window.open(result.url, '_blank');
          showToast('Document generated and saved to case file', 'success');
        } else if (result.success && result instanceof Blob) {
          downloadBlob(genRes);
          showToast('Document generated', 'success');
        } else {
          throw new Error(result.error || 'Generation failed');
        }
      } else {
        // Merge field overrides into fieldData
        const mergedFieldData = { ...sd.fieldData, ...overrides };

        // Backend loads the DOCX template from S3 itself — no need to send it
        const genRes = await api.post('/api/documents/generate', {
          caseRef,
          templateName: sd.templateName,
          fieldData: mergedFieldData,
        });

        const result = genRes.data;
        if (result.success && result.url) {
          window.open(result.url, '_blank');
          showToast('Document generated and saved to case file', 'success');
        } else {
          throw new Error(result.error || 'Generation failed');
        }
      }

      setFieldValidationOpen(false);
      setDraftModalOpen(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to generate document';
      showToast(msg, 'error');
    } finally {
      setDraftGenerating(false);
    }
  };

  // "Continue" button — use field values as-is for this generation only
  const handleFieldValidationContinue = async () => {
    if (!scanData) return;
    await generateDocumentWithScan(scanData, missingFieldValues);
  };

  // "Save & Continue" button — save values to DB then generate
  const handleFieldValidationSaveAndContinue = async () => {
    if (!scanData || !caseRef) return;
    setFieldSaving(true);
    try {
      // Build fieldValues from the user-entered overrides
      const fieldValues: Record<string, string> = {};
      for (const [key, val] of Object.entries(missingFieldValues)) {
        if (val) fieldValues[key] = val;
      }

      if (Object.keys(fieldValues).length > 0) {
        const isDat = scanData.templateName.startsWith('dat_');
        if (isDat) {
          const datId = scanData.templateName.replace('dat_', '');
          await api.post(`/api/documents/dat-save-fields/${datId}`, { caseRef, fieldValues });
        } else {
          await api.post('/api/documents/save-fields', {
            caseRef,
            templateName: scanData.templateName,
            fieldValues,
          });
        }
        showToast('Field data saved', 'success');
      }

      await generateDocumentWithScan(scanData, missingFieldValues);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save fields';
      showToast(msg, 'error');
    } finally {
      setFieldSaving(false);
    }
  };

  // Helper to download blob from axios response
  function downloadBlob(genRes: { data: unknown; headers: AxiosResponseHeaders }) {
    const blob = genRes.data as Blob;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const disposition = String(genRes.headers['content-disposition'] || '');
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    a.download = filenameMatch?.[1] || `${caseRef}_letter.docx`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Email preview + send
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [emailPreview, setEmailPreview] = useState<{
    templateId: string;
    recipient_email: string;
    subject: string;
    body_html: string;
    footer_html: string;
    unresolved: string[];
  } | null>(null);
  const [emailSending, setEmailSending] = useState(false);

  const handlePreviewEmail = async () => {
    if (!selectedEmailTemplateId || !caseRef) return;
    setDraftGenerating(true);
    try {
      const res = await api.post(`/api/documents/da-email-preview/${selectedEmailTemplateId}`, {
        caseRef,
        manual_values: {},
      });
      const d = res.data;
      if (!d.success) throw new Error(d.error || 'Preview failed');
      setEmailPreview({
        templateId: selectedEmailTemplateId,
        recipient_email: d.recipient_email || '',
        subject: d.subject || '',
        body_html: d.body_html || '',
        footer_html: d.footer_html || '',
        unresolved: d.unresolved_tokens || [],
      });
      setDraftModalOpen(false);
      setEmailPreviewOpen(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to preview email';
      showToast(msg, 'error');
    } finally {
      setDraftGenerating(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailPreview || !caseRef) return;
    setEmailSending(true);
    try {
      const res = await api.post(`/api/documents/da-email-send/${emailPreview.templateId}`, {
        caseRef,
        manual_values: {},
        attachments: [],
      });
      if (!res.data.success) throw new Error(res.data.error || 'Send failed');
      showToast(`Email sent to ${res.data.recipient_email || 'recipient'}`, 'success');
      setEmailPreviewOpen(false);
      setEmailPreview(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to send email';
      showToast(msg, 'error');
    } finally {
      setEmailSending(false);
    }
  };

  /* ── Complete Task → Risk Assessment ── */
  const [completeTaskModalOpen, setCompleteTaskModalOpen] = useState(false);
  const [completeTaskType, setCompleteTaskType] = useState('');
  const [riskModalOpen, setRiskModalOpen] = useState(false);
  const [riskForm, setRiskForm] = useState({
    clientDetailsVerified: '',
    vulnerableClient: '',
    idPresent: '',
    poaPresent: '',
    amlPresent: '',
    riskLevel: '',
    caseOutcome: '',
    rejectReason: '',
    extraComments: '',
  });
  const [riskTimerRunning, setRiskTimerRunning] = useState(false);
  const [riskTimerStart, setRiskTimerStart] = useState<number | null>(null);
  const [riskTimerElapsed, setRiskTimerElapsed] = useState(0);
  const riskTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (riskTimerRunning && riskTimerStart) {
      riskTimerRef.current = setInterval(() => {
        setRiskTimerElapsed(Date.now() - riskTimerStart);
      }, 1000);
      return () => {
        if (riskTimerRef.current) clearInterval(riskTimerRef.current);
      };
    }
  }, [riskTimerRunning, riskTimerStart]);

  const handleOpenSelectedTask = () => {
    if (completeTaskType === 'risk-assessment') {
      setCompleteTaskModalOpen(false);
      setRiskModalOpen(true);
      setRiskTimerElapsed(0);
      setRiskTimerRunning(false);
      setRiskTimerStart(null);
    } else if (completeTaskType === 'pre-issue-checklist') {
      setCompleteTaskModalOpen(false);
      setPreIssueModalOpen(true);
      setPreIssueTimerElapsed(0);
      setPreIssueTimerRunning(false);
      setPreIssueTimerStart(null);
    } else {
      showToast('Please select a task type', 'error');
    }
  };

  const handleSaveRiskAssessment = async () => {
    const {
      clientDetailsVerified,
      vulnerableClient,
      idPresent,
      poaPresent,
      amlPresent,
      riskLevel,
      caseOutcome,
    } = riskForm;
    if (
      !clientDetailsVerified ||
      !vulnerableClient ||
      !idPresent ||
      !poaPresent ||
      !amlPresent ||
      !riskLevel ||
      !caseOutcome
    ) {
      showToast('Please fill in all required fields', 'error');
      return;
    }
    setRiskTimerRunning(false);
    if (riskTimerRef.current) clearInterval(riskTimerRef.current);

    const timeSpent = formatElapsed(riskTimerElapsed);
    try {
      await saveRiskAssessment.mutateAsync({
        caseRef,
        taskType: 'Risk Assessment',
        ...riskForm,
        timeSpent,
        timeSpentSeconds: Math.floor(riskTimerElapsed / 1000),
      });
      showToast('Risk Assessment saved', 'success');
      setRiskModalOpen(false);
      setRiskForm({
        clientDetailsVerified: '',
        vulnerableClient: '',
        idPresent: '',
        poaPresent: '',
        amlPresent: '',
        riskLevel: '',
        caseOutcome: '',
        rejectReason: '',
        extraComments: '',
      });
    } catch {
      showToast('Failed to save risk assessment', 'error');
    }
  };

  /* ── Complete Task → Pre-Issue Checklist ── */
  const [preIssueModalOpen, setPreIssueModalOpen] = useState(false);
  const [preIssueForm, setPreIssueForm] = useState({
    form_input_fees: '',
    form_input_consent: '',
    form_input_cl_details: '',
    form_input_def_details: '',
    form_input_scf: '',
    form_input_poc: '',
    form_input_cov_let: '',
    form_input_cert: '',
    form_input_res_pack: '',
    form_input_comments: '',
  });
  const [preIssueTimerRunning, setPreIssueTimerRunning] = useState(false);
  const [preIssueTimerStart, setPreIssueTimerStart] = useState<number | null>(null);
  const [preIssueTimerElapsed, setPreIssueTimerElapsed] = useState(0);
  const preIssueTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (preIssueTimerRunning && preIssueTimerStart) {
      preIssueTimerRef.current = setInterval(() => {
        setPreIssueTimerElapsed(Date.now() - preIssueTimerStart);
      }, 1000);
      return () => {
        if (preIssueTimerRef.current) clearInterval(preIssueTimerRef.current);
      };
    }
  }, [preIssueTimerRunning, preIssueTimerStart]);

  const handleSavePreIssueChecklist = async () => {
    // Validate all required checklist fields
    const requiredFields = [
      'form_input_fees',
      'form_input_consent',
      'form_input_cl_details',
      'form_input_def_details',
      'form_input_scf',
      'form_input_poc',
      'form_input_cov_let',
      'form_input_cert',
      'form_input_res_pack',
    ];

    const missingFields = requiredFields.filter(
      (field) => !preIssueForm[field as keyof typeof preIssueForm],
    );
    if (missingFields.length > 0) {
      showToast('Please complete all checklist items', 'error');
      return;
    }

    setPreIssueTimerRunning(false);
    if (preIssueTimerRef.current) clearInterval(preIssueTimerRef.current);

    const timeSpent = formatElapsed(preIssueTimerElapsed);
    try {
      await savePreIssueChecklist.mutateAsync({
        caseRef,
        taskType: 'Pre-Issue Checklist',
        ...preIssueForm,
        timeSpent,
        timeSpentSeconds: Math.floor(preIssueTimerElapsed / 1000),
      });
      showToast('Pre-Issue Checklist saved', 'success');
      setPreIssueModalOpen(false);
      setPreIssueForm({
        form_input_fees: '',
        form_input_consent: '',
        form_input_cl_details: '',
        form_input_def_details: '',
        form_input_scf: '',
        form_input_poc: '',
        form_input_cov_let: '',
        form_input_cert: '',
        form_input_res_pack: '',
        form_input_comments: '',
      });
    } catch {
      showToast('Failed to save pre-issue checklist', 'error');
    }
  };

  /* ── Aircall call ── */
  const [aircallModalOpen, setAircallModalOpen] = useState(false);
  const [aircallState, setAircallState] = useState<'confirm' | 'calling' | 'success' | 'error'>(
    'confirm',
  );
  const [aircallError, setAircallError] = useState('');

  const handleCallClient = () => {
    const phone = contact?.['Phone (main)'] || contact?.Phone || contact?.phone;
    if (!phone) {
      showToast('No phone number available', 'error');
      return;
    }
    if (!validatePhoneNumber(String(phone))) {
      showToast('Phone number invalid – check details', 'error');
      return;
    }
    const username = user?.username || '';
    const aircallId = getAircallUserId(username);
    if (!aircallId) {
      showToast('Err: Invalid User ID, refer to system admin', 'error');
      return;
    }
    setAircallState('confirm');
    setAircallError('');
    setAircallModalOpen(true);
  };

  const handleConfirmCall = async () => {
    const phone = String(contact?.['Phone (main)'] || contact?.Phone || contact?.phone || '');
    const username = user?.username || '';
    const aircallId = getAircallUserId(username);
    if (!aircallId) return;

    setAircallState('calling');
    try {
      const res = await fetch(`${AIRCALL_API_GATEWAY}/api/aircall/call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: aircallId, phoneNumber: formatPhoneE164(phone) }),
      });
      const result = await res.json();
      if (result.success) {
        setAircallState('success');
        showToast('Call initiated', 'success');
        setTimeout(() => setAircallModalOpen(false), 3000);
      } else {
        throw new Error(result.message || result.error || 'Unknown error');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unable to initiate call';
      setAircallState('error');
      setAircallError(msg.includes('unavailable') ? 'Please set Aircall status to Available' : msg);
      showToast(msg, 'error');
    }
  };

  /* ── Email handlers ── */
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailTarget, setEmailTarget] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isOutlookConnected, setIsOutlookConnected] = useState(false);
  const [outlookSignature, setOutlookSignature] = useState('');

  useEffect(() => {
    const checkOutlook = async () => {
      try {
        const res = await fetch(`${CONFIG.BACKEND_BASE_URL}/api/emails/check-connection`, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
        });
        const data = await res.json();
        setIsOutlookConnected(data.connected);
        if (data.signature) {
          setOutlookSignature(data.signature);
        }
      } catch (err) {
        setIsOutlookConnected(false);
      }
    };
    checkOutlook();
  }, [emailModalOpen]);

  const handleConnectOutlook = async () => {
    try {
      const res = await fetch(`${CONFIG.BACKEND_BASE_URL}/api/emails/connect-outlook`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (data.url) {
        // Add listener for the popup message
        const handleMessage = (event: MessageEvent) => {
          if (event.data === 'outlook-connected') {
            showToast('Outlook connected successfully!', 'success');
            setIsOutlookConnected(true);
            window.removeEventListener('message', handleMessage);
          }
        };
        window.addEventListener('message', handleMessage);
        window.open(data.url, 'outlook-connect', 'width=600,height=600');
      }
    } catch (err) {
      showToast('Failed to start Outlook connection', 'error');
    }
  };

  const handleSendOutlookEmail = async (data: {
    to: string;
    subject: string;
    body: string;
    attachments: EmailAttachment[];
    includeSignature?: boolean;
  }) => {
    setIsSendingEmail(true);

    // Log attachment details for debugging
    console.log('Sending email with attachments:', {
      attachmentCount: data.attachments?.length || 0,
      attachments: data.attachments?.map((a) => {
        const contentSize =
          typeof a.content === 'string' ? a.content.length : a.content?.byteLength || 0;
        return {
          name: a.name,
          type: a.type,
          contentLength: contentSize,
          sizeKB: Math.round((contentSize * 0.75) / 1024),
        };
      }),
    });

    try {
      const payload = {
        ...data,
        caseRef,
        caseType: caseTypeName,
      };

      const payloadString = JSON.stringify(payload);
      const payloadSizeMB = (payloadString.length / (1024 * 1024)).toFixed(2);
      console.log('Email payload size:', {
        bytes: payloadString.length,
        MB: payloadSizeMB,
        attachmentCount: data.attachments?.length || 0,
      });

      const res = await fetch(`${CONFIG.BACKEND_BASE_URL}/api/emails/send-outlook`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionStorage.getItem('token')}`,
        },
        body: payloadString,
      }).catch((fetchError) => {
        console.error('Fetch failed:', {
          error: fetchError,
          message: fetchError.message,
          name: fetchError.name,
          payloadSizeMB,
        });
        throw new Error(
          `Network request failed. Payload size: ${payloadSizeMB} MB. ${fetchError.message}`,
        );
      });

      // Check for HTTP errors before parsing JSON
      if (!res.ok) {
        if (res.status === 413) {
          throw new Error(
            'Email payload too large. Please reduce attachment size or remove some attachments.',
          );
        }
        // Try to get error message from response
        let errorMsg = `Server error: ${res.status} ${res.statusText}`;
        try {
          const errorData = await res.json();
          if (errorData.error) errorMsg = errorData.error;
        } catch {
          // If JSON parsing fails, use default error
        }
        throw new Error(errorMsg);
      }

      const result = await res.json();

      console.log('Email send response:', {
        success: result.success,
        error: result.error,
        status: res.status,
      });

      if (result.success) {
        showToast('Email sent and archived to case', 'success');
        setEmailModalOpen(false);
      } else {
        throw new Error(result.error || 'Failed to send email');
      }
    } catch (err: unknown) {
      console.error('Email send error:', err);
      showToast(getErrorMessage(err), 'error');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleEmailClient = () => {
    const email = String(contact?.Email || contact?.email || '');
    if (!email || email.trim() === '') {
      showToast('No client email available', 'error');
      return;
    }
    setEmailTarget(email);
    showToast('Opening email client…', 'success');
    setEmailModalOpen(true);
  };

  const handleEmailDefendant = () => {
    const email = String(caseData?.defendant_email || getFieldValue('defendant_email') || '');
    if (!email || email.trim() === '') {
      showToast('No defendant email available', 'error');
      return;
    }
    setEmailTarget(email);
    showToast('Opening email client…', 'success');
    setEmailModalOpen(true);
  };

  const handleEmailDefSol = () => {
    const email = String(getFieldValue('def_sol_email') || caseData?.def_sol_email || '');
    if (!email || email.trim() === '') {
      showToast('No defendant solicitor email available', 'error');
      return;
    }
    setEmailTarget(email);
    showToast('Opening email client…', 'success');
    setEmailModalOpen(true);
  };

  /* ── Contact actions menu toggle ── */
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [emailSubMenuOpen, setEmailSubMenuOpen] = useState(false);

  /* ── Reassign contact ── */
  const [reassignModalOpen, setReassignModalOpen] = useState(false);

  /* ── View audit ── */
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditLimit, setAuditLimit] = useState(200);
  const { data: auditData, isLoading: auditLoading } = useCaseAuditLog(caseRef, showAuditModal, {
    limit: auditLimit,
  });
  const auditEntries = auditData?.rows ?? [];
  const hasMoreAudit = auditData ? auditData.count >= auditLimit : false;
  const loadMoreAudit = () => setAuditLimit((prev) => prev + 200);

  const [reassignSearch, setReassignSearch] = useState('');
  const [reassignResults, setReassignResults] = useState<ContactSearchResult[]>([]);
  const [reassignSelected, setReassignSelected] = useState<ContactSearchResult | null>(null);
  const reassignSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleReassignSearch = (query: string) => {
    setReassignSearch(query);
    if (reassignSearchTimer.current) clearTimeout(reassignSearchTimer.current);
    if (query.length < 2) {
      setReassignResults([]);
      return;
    }
    reassignSearchTimer.current = setTimeout(async () => {
      try {
        const results = await searchContacts.mutateAsync(query);
        setReassignResults(results as ContactSearchResult[]);
      } catch {
        setReassignResults([]);
      }
    }, 300);
  };

  const handleReassignSave = async () => {
    if (!reassignSelected || !caseRef) return;
    try {
      await reassignCase.mutateAsync(reassignSelected.ContactID);
      showToast(`Case reassigned to ${reassignSelected.name}`, 'success');
      setReassignModalOpen(false);
      setReassignSelected(null);
      setReassignSearch('');
      setReassignResults([]);
    } catch {
      showToast('Failed to reassign contact', 'error');
    }
  };

  /* ── Save to recent cases on load ── */
  useEffect(() => {
    if (!caseRef || !caseData) return;
    const recentCase = {
      caseRef,
      caseType: caseTypeName || '',
      clientName:
        caseData.client_name ||
        caseData.ClientName ||
        (contact ? contactName(contact as Record<string, unknown>) : ''),
      defendant: caseData.defendant_name || '',
      url: window.location.href,
    };
    try {
      let recentCases: Array<{ caseRef: string; [k: string]: unknown }> = [];
      try {
        recentCases = JSON.parse(localStorage.getItem('recentCases') || '[]');
      } catch {
        recentCases = [];
      }
      recentCases = recentCases.filter((c) => c.caseRef !== caseRef);
      recentCases.unshift(recentCase);
      if (recentCases.length > 20) recentCases = recentCases.slice(0, 20);
      localStorage.setItem('recentCases', JSON.stringify(recentCases));
    } catch {
      /* ignore */
    }
  }, [caseRef, caseData, caseTypeName, contact]);

  /* ── Unsaved-changes guard ── */
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  /* ── Defendant solicitor auto-populate ── */
  const handleDefSolChange = useCallback(
    (defSolId: string) => {
      setField('def_sol_id', defSolId);
      const sol = defSolicitors.find((ds) => String(ds.defendant_solicitor_id) === defSolId);
      if (sol) {
        const addr = [
          sol.def_sol_address_line_1,
          sol.def_sol_address_line_2,
          sol.def_sol_address_line_3,
          sol.def_sol_address_postcode,
        ]
          .filter(Boolean)
          .join(', ');
        setField('def_sol_address', addr);
        setField('def_sol_email', sol.def_sol_email || '');
        setField('def_sol_phone', sol.def_sol_phone || '');
      }
    },
    [defSolicitors, setField],
  );

  /* ── Defendant auto-populate address fields when defendant changes ── */
  const handleDefendantChange = useCallback(
    (defId: string) => {
      setField('defendant_id', defId);
      // The API returns addresses[] on each defendant even though the TS type doesn't declare it
      const def = (
        defendants as unknown as Array<{
          defendant_id: number;
          addresses?: Array<{
            address_type: string;
            address_line_1?: string;
            address_line_2?: string;
            address_line_3?: string;
            postcode?: string;
          }>;
        }>
      ).find((d) => String(d.defendant_id) === defId);
      if (def?.addresses?.length) {
        const addr = def.addresses.find((a) => a.address_type === 'Claim Form') || def.addresses[0];
        setField('defendant_address_line_1', addr.address_line_1 || '');
        setField('defendant_address_line_2', addr.address_line_2 || '');
        setField('defendant_address_line_3', addr.address_line_3 || '');
        setField('defendant_address_postcode', addr.postcode || '');
      }
    },
    [defendants, setField],
  );

  /* ── Export timesheet ── */
  const handleExportTimesheet = async () => {
    if (!caseRef) return;
    try {
      const res = await api.get(`/api/casefile/${caseRef}/timesheet`);
      const items = (res.data?.timesheet?.items || res.data?.items || []) as Array<
        Record<string, unknown>
      >;
      if (items.length === 0) {
        showToast('No timesheet entries found', 'error');
        return;
      }

      const fmt = (v: unknown) => {
        if (!v) return '';
        const d = new Date(String(v));
        return isNaN(d.getTime())
          ? String(v)
          : d.toLocaleString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            });
      };
      const headers = ['Date', 'Start Time', 'End Time', 'Duration', 'Units', 'Task', 'User'];
      const rows = items.map((row) =>
        [
          fmt(row.Timestamp),
          fmt(row.StartTime),
          fmt(row.EndTime),
          row.Duration,
          row.Units,
          row.Task,
          row.User,
        ]
          .map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`)
          .join(','),
      );
      const csv = [headers.join(','), ...rows].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${caseRef}_timesheet.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('Timesheet exported', 'success');
    } catch {
      showToast('Failed to export timesheet', 'error');
    }
  };

  /* ── Render a single dynamic field ── */
  const renderField = (field: FieldDef) => {
    const val = getFieldValue(field.key);
    const ft = (field.field_type || '').toLowerCase();
    const isDynamic = !!field.validation_rules?.dynamic;
    const source = field.validation_rules?.source;
    const isReadonly = !!field.validation_rules?.readonly;

    // Dropdown with dynamic source
    if (isDynamic && source) {
      if (source === 'solicitors') {
        const options = solicitors.map((sol) => ({
          value: sol.cognito_user_id || sol.username,
          label: sol.displayName || sol.display_name || sol.username,
        }));
        // assigned_solicitor / assigned_user_id are the canonical cases-table fields;
        // every other solicitor-sourced field (e.g. secondary_fee_earner) is EAV and uses its own key.
        const isAssignedSolicitor =
          field.key === 'assigned_solicitor' || field.key === 'assigned_user_id';
        const readKey = isAssignedSolicitor ? 'assigned_user_id' : field.key;
        const writeKey = isAssignedSolicitor ? 'assigned_user_id' : field.key;
        return (
          <div key={field.key} className={s.field}>
            <label>{field.name}</label>
            <SearchableSelect
              options={options}
              value={String(getFieldValue(readKey) ?? '')}
              onChange={(v) => setField(writeKey, v)}
              placeholder="Select Solicitor…"
            />
          </div>
        );
      }

      if (source === 'stages') {
        const options = stages.map((st) => ({
          value: st.stage_name,
          label: st.stage_name,
        }));
        return (
          <div key={field.key} className={s.field}>
            <label>{field.name}</label>
            <div className={s.fieldWithAction}>
              <SearchableSelect
                options={options}
                value={String(val ?? '')}
                onChange={(v) => setField(field.key, v)}
                placeholder="Select Stage…"
                disabled={!canEditStage}
              />
              <button
                type="button"
                className={s.fieldActionBtn}
                title="View stage history"
                onClick={() => setShowStageHistory(true)}
              >
                <FontAwesomeIcon icon={faHistory} />
              </button>
            </div>
          </div>
        );
      }

      if (source === 'defendants') {
        const options = defendants.map((d) => ({
          value: String(d.defendant_id),
          label: d.defendant_name,
        }));
        return (
          <div key={field.key} className={s.field}>
            <label>{field.name}</label>
            <SearchableSelect
              options={options}
              value={String(getFieldValue('defendant_id') ?? '')}
              onChange={(v) => handleDefendantChange(v)}
              placeholder="Select Defendant…"
            />
          </div>
        );
      }

      if (source === 'defendant_solicitors') {
        const options = defSolicitors.map((ds) => ({
          value: String(ds.defendant_solicitor_id),
          label: ds.def_sol_firm_name,
        }));
        return (
          <div key={field.key} className={s.field}>
            <label>{field.name}</label>
            <SearchableSelect
              options={options}
              value={String(getFieldValue('def_sol_id') ?? '')}
              onChange={(v) => handleDefSolChange(v)}
              placeholder="Select Defendant Solicitor…"
            />
          </div>
        );
      }
    }

    // Static dropdown (field_type is 'dropdown' or 'select')
    if (ft === 'dropdown' || ft === 'select') {
      // Use options from field_options table (returned by backend), fallback to validation_rules.options
      const fieldOpts = field.options ?? [];
      const vrOpts = (field.validation_rules?.options as string[] | undefined) ?? [];
      const options =
        fieldOpts.length > 0
          ? fieldOpts.map((opt) => ({ value: opt.value, label: opt.label || opt.value }))
          : vrOpts.map((opt) => ({ value: opt, label: opt }));
      return (
        <div key={field.key} className={s.field}>
          <label>{field.name}</label>
          <SearchableSelect
            options={options}
            value={String(val ?? '')}
            onChange={(v) => setField(field.key, v)}
            placeholder="Select…"
            disabled={isReadonly}
          />
        </div>
      );
    }

    // Date
    if (ft === 'date') {
      return (
        <div key={field.key} className={s.field}>
          <label>{field.name}</label>
          <DatePicker
            value={toInputDate(val)}
            onChange={(v) => setField(field.key, v)}
            readOnly={isReadonly}
            className={isReadonly ? s.readonlyInput : undefined}
          />
        </div>
      );
    }

    // Number / currency
    if (ft === 'number' || ft === 'currency') {
      return (
        <div key={field.key} className={s.field}>
          <label>{field.name}</label>
          <input
            type="number"
            value={val != null ? String(val) : ''}
            onChange={(e) => setField(field.key, e.target.value)}
            readOnly={isReadonly}
            className={isReadonly ? s.readonlyInput : undefined}
          />
        </div>
      );
    }

    // Default: text
    return (
      <div key={field.key} className={s.field}>
        <label>{field.name}</label>
        <input
          type="text"
          value={val != null ? String(val) : ''}
          onChange={(e) => setField(field.key, e.target.value)}
          readOnly={isReadonly}
          className={isReadonly ? s.readonlyInput : undefined}
        />
      </div>
    );
  };

  /* ── Warn-before-navigate ref for router ── */
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  /* ═══════════════════ RENDER ═══════════════════ */

  if (isLoading) {
    return (
      <div className={s.loading}>
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        <p>Loading case…</p>
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <div className={s.errorState}>
        <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
        <h2>Failed to load case</h2>
        <p>{(error as Error)?.message || 'Case not found'}</p>
        <button onClick={() => navigate('/cases')}>Back to Cases</button>
      </div>
    );
  }

  /* ── Permission guard: block users who lack access to this case type ── */
  if (allowedCaseTypes !== null && allowedCaseTypes !== undefined && caseTypeName) {
    const permitted = allowedCaseTypes.some((p) => p.toLowerCase() === caseTypeName.toLowerCase());
    if (!permitted) {
      return (
        <Navigate to="/permission-blocked" state={{ caseType: caseTypeName, caseRef }} replace />
      );
    }
  }

  const clientName =
    caseData.client_name ||
    caseData.ClientName ||
    (contact ? contactName(contact as Record<string, unknown>) : 'Unknown');
  const defendantName =
    caseData.defendant_name || caseData.DefendantName || caseData.defendant || '';
  const headerTitle = defendantName ? `${clientName} vs ${defendantName}` : clientName;

  return (
    <div className={s.page}>
      {/* ══════════ CLIENT HEADER CARD ══════════ */}
      <ClientHeaderCard
        headerTitle={headerTitle}
        contact={contact as Record<string, unknown>}
        actionsMenuOpen={actionsMenuOpen}
        setActionsMenuOpen={setActionsMenuOpen}
        emailSubMenuOpen={emailSubMenuOpen}
        setEmailSubMenuOpen={setEmailSubMenuOpen}
        handleCallClient={handleCallClient}
        handleEmailClient={handleEmailClient}
        handleEmailDefendant={handleEmailDefendant}
        handleEmailDefSol={handleEmailDefSol}
        handleConnectOutlook={handleConnectOutlook}
        isOutlookConnected={isOutlookConnected}
        setReassignModalOpen={setReassignModalOpen}
        setViewAuditOpen={setShowAuditModal}
        canReassign={canReassign}
        canViewAudit={canViewAudit}
        canEmail={canSendEmail}
        fmtUKDate={fmtUKDate}
      />

      {/* ══════════ ACTION BAR ══════════ */}
      <ActionBar
        dirty={dirty}
        handleSave={handleSave}
        isSavePending={updateCase.isPending}
        caseRef={caseRef || ''}
        caseData={caseData as Record<string, unknown>}
        canRecordTime={canRecordTime}
        canExportData={canExportData}
        canCreateTask={canCreateTask}
        canDeleteCase={canDeleteCase}
        getFieldValue={getFieldValue}
        setField={setField}
        onRecordTime={() => setTimeModalOpen(true)}
        onAddNote={() => setNoteModalOpen(true)}
        onDraftAssistant={() => setDraftModalOpen(true)}
        onAddTask={openTaskModal}
        onCompleteTask={() => setCompleteTaskModalOpen(true)}
        onDeleteCase={() => setDeleteCaseModalOpen(true)}
        handleExportTimesheet={handleExportTimesheet}
        clioMatterId={(getFieldValue('clio_matter_id') as string | null | undefined) || null}
        isClioSyncing={isClioSyncing}
        onSyncToClio={handleSyncToClio}
        onForceSyncToClio={handleForceSyncToClio}
      />

      {/* ══════════ DYNAMIC CASE FIELDS ══════════ */}
      <div className={s.fieldsContainer}>
        {LAYOUT_PLAN.map((group, gi) => {
          // Check if any category in this group has fields
          const hasFields = group.some((cat) => categorised[cat]?.length);
          if (!hasFields) return null;

          return (
            <div key={gi} className={s.fieldRow}>
              {group.map((category) => {
                const fields = categorised[category];
                if (!fields?.length) return null;
                const isCollapsible = COLLAPSIBLE_CATEGORIES.has(category);
                const isOpen = expandedCats.has(category);

                return (
                  <div
                    key={category}
                    className={s.fieldCategory}
                    style={{ flex: group.length === 2 ? '1 1 0' : '0 0 100%', minWidth: 0 }}
                  >
                    <div
                      className={`${s.categoryHeader} ${isCollapsible ? s.categoryCollapsible : ''}`}
                      onClick={isCollapsible ? () => toggleCategory(category) : undefined}
                    >
                      <h3>{category}</h3>
                      {isCollapsible && (
                        <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} />
                      )}
                    </div>

                    {(!isCollapsible || isOpen) && (
                      <div className={s.fieldGrid}>{fields.map(renderField)}</div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* ══════════ TASKS TABLE ══════════ */}
      <TasksTable
        tasks={tasks}
        currentUserId={user?.sub}
        openEditTask={openEditTask}
        fmtUKDate={fmtUKDate}
      />

      {/* ══════════ NOTES TABLE ══════════ */}
      <NotesTable notes={notes} fmtUKDate={fmtUKDate} />

      {/* ══════════ PORTAL NOTES (client-visible) ══════════ */}
      <PortalNotes
        portalNotes={portalNotesData?.notes ?? []}
        portalNoteText={portalNoteText}
        setPortalNoteText={setPortalNoteText}
        onPost={async () => {
          try {
            await createPortalNote.mutateAsync(portalNoteText);
            setPortalNoteText('');
            showToast('Portal note posted', 'success');
          } catch {
            showToast('Failed to post note', 'error');
          }
        }}
        onDelete={async (id) => {
          try {
            await deletePortalNote.mutateAsync(id);
            showToast('Portal note deleted', 'success');
          } catch {
            showToast('Failed to delete note', 'error');
          }
        }}
        isPostPending={createPortalNote.isPending}
        fmtUKDate={fmtUKDate}
      />

      {/* ══════════ STAGE HISTORY MODAL ══════════ */}
      {showStageHistory && (
        <StageHistoryModal
          caseRef={caseRef || ''}
          stageHistory={stageHistory}
          onClose={() => setShowStageHistory(false)}
        />
      )}

      {/* ══════════ DELETE CASE MODAL (superadmin only) ══════════ */}
      {deleteCaseModalOpen && (
        <DeleteCaseModal
          caseRef={caseRef || ''}
          handleDeleteCase={handleDeleteCase}
          isPending={deleteCaseMut.isPending}
          onClose={() => setDeleteCaseModalOpen(false)}
        />
      )}

      {/* ══════════ NOTE MODAL ══════════ */}
      {noteModalOpen && (
        <NoteModal
          noteText={noteText}
          setNoteText={setNoteText}
          noteContact={noteContact}
          setNoteContact={setNoteContact}
          handleAddNote={handleAddNote}
          isPending={addNote.isPending}
          onClose={() => setNoteModalOpen(false)}
        />
      )}

      {/* ══════════ TASK MODAL ══════════ */}
      {taskModalOpen && (
        <CreateTaskModal
          taskForm={taskForm}
          setTaskForm={setTaskForm}
          handleCreateTask={handleCreateTask}
          solicitors={solicitors}
          isPending={createTask.isPending}
          onClose={() => setTaskModalOpen(false)}
        />
      )}

      {/* ══════════ EDIT TASK MODAL ══════════ */}
      {editTask && (
        <EditTaskModal
          editTaskForm={editTaskForm}
          setEditTaskForm={setEditTaskForm}
          handleUpdateTask={handleUpdateTask}
          handleDeleteTask={handleDeleteTask}
          isUpdatePending={updateTaskMut.isPending}
          isDeletePending={deleteTaskMut.isPending}
          onClose={() => setEditTask(null)}
        />
      )}

      {/* ══════════ RECORD TIME MODAL ══════════ */}
      {timeModalOpen &&
        (CONFIG.TR_BILLING_V2_FORM_ENABLED ? (
          <RecordTimeModalV2
            caseRef={caseRef || ''}
            username={user?.username || ''}
            timeTaskDesc={timeTaskDesc}
            setTimeTaskDesc={setTimeTaskDesc}
            timeManualUnits={timeManualUnits}
            setTimeManualUnits={setTimeManualUnits}
            activityCodeId={timeActivityCodeId}
            setActivityCodeId={setTimeActivityCodeId}
            billable={timeBillable}
            setBillable={setTimeBillable}
            handleStartTimer={handleStartTimer}
            handleSaveEntry={handleSaveEntry}
            onClose={() => setTimeModalOpen(false)}
          />
        ) : (
          <RecordTimeModal
            caseRef={caseRef || ''}
            username={user?.username || ''}
            timeTaskDesc={timeTaskDesc}
            setTimeTaskDesc={setTimeTaskDesc}
            timeManualUnits={timeManualUnits}
            setTimeManualUnits={setTimeManualUnits}
            handleStartTimer={handleStartTimer}
            onClose={() => setTimeModalOpen(false)}
          />
        ))}

      {/* ══════════ DRAFT ASSISTANT MODAL ══════════ */}
      {draftModalOpen && (
        <DraftAssistantModal
          draftTab={draftTab}
          setDraftTab={setDraftTab}
          selectedTemplateId={selectedTemplateId}
          setSelectedTemplateId={setSelectedTemplateId}
          selectedEmailTemplateId={selectedEmailTemplateId}
          setSelectedEmailTemplateId={setSelectedEmailTemplateId}
          draftGenerating={draftGenerating}
          draftTemplates={draftTemplates}
          emailTemplates={emailTemplates}
          handleGenerateLetter={handleGenerateLetter}
          handlePreviewEmail={handlePreviewEmail}
          canSendEmail={canSendEmail}
          onClose={() => setDraftModalOpen(false)}
        />
      )}

      {/* ══════════ EMAIL DRAFTER MODAL (Outlook API) ══════════ */}
      <EmailDrafterModal
        isOpen={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        caseRef={caseRef || ''}
        defaultTo={emailTarget}
        onSend={handleSendOutlookEmail}
        isSending={isSendingEmail}
        isOutlookConnected={isOutlookConnected}
        signature={outlookSignature}
      />

      {/* ══════════ FIELD VALIDATION MODAL ══════════ */}
      {fieldValidationOpen && (
        <FieldValidationModal
          fieldValidationPhase={fieldValidationPhase}
          scanProgress={scanProgress}
          scanData={scanData}
          missingFieldValues={missingFieldValues}
          setMissingFieldValues={setMissingFieldValues}
          draftGenerating={draftGenerating}
          fieldSaving={fieldSaving}
          handleFieldValidationContinue={handleFieldValidationContinue}
          handleFieldValidationSaveAndContinue={handleFieldValidationSaveAndContinue}
          onClose={() => {
            setFieldValidationOpen(false);
            setDraftGenerating(false);
          }}
        />
      )}

      {/* ══════════ EMAIL PREVIEW MODAL ══════════ */}
      {emailPreviewOpen && emailPreview && (
        <EmailPreviewModal
          emailPreview={emailPreview}
          emailSending={emailSending}
          handleSendEmail={handleSendEmail}
          onClose={() => {
            setEmailPreviewOpen(false);
            setEmailPreview(null);
          }}
        />
      )}

      {/* ══════════ COMPLETE TASK MODAL ══════════ */}
      {completeTaskModalOpen && (
        <CompleteTaskModal
          completeTaskType={completeTaskType}
          setCompleteTaskType={setCompleteTaskType}
          handleOpenSelectedTask={handleOpenSelectedTask}
          onClose={() => setCompleteTaskModalOpen(false)}
        />
      )}

      {/* ══════════ RISK ASSESSMENT MODAL ══════════ */}
      {riskModalOpen && (
        <RiskAssessmentModal
          riskForm={riskForm}
          setRiskForm={setRiskForm}
          riskTimerRunning={riskTimerRunning}
          riskTimerElapsed={riskTimerElapsed}
          riskTimerStart={riskTimerStart}
          setRiskTimerStart={setRiskTimerStart}
          setRiskTimerRunning={setRiskTimerRunning}
          riskTimerRef={riskTimerRef}
          handleSaveRiskAssessment={handleSaveRiskAssessment}
          isPending={saveRiskAssessment.isPending}
          formatElapsed={formatElapsed}
          onClose={() => setRiskModalOpen(false)}
        />
      )}

      {/* ══════════ PRE-ISSUE CHECKLIST MODAL ══════════ */}
      {preIssueModalOpen && (
        <PreIssueChecklistModal
          preIssueForm={preIssueForm}
          setPreIssueForm={setPreIssueForm}
          preIssueTimerRunning={preIssueTimerRunning}
          preIssueTimerElapsed={preIssueTimerElapsed}
          preIssueTimerStart={preIssueTimerStart}
          setPreIssueTimerStart={setPreIssueTimerStart}
          setPreIssueTimerRunning={setPreIssueTimerRunning}
          preIssueTimerRef={preIssueTimerRef}
          handleSavePreIssueChecklist={handleSavePreIssueChecklist}
          isPending={savePreIssueChecklist.isPending}
          formatElapsed={formatElapsed}
          onClose={() => setPreIssueModalOpen(false)}
        />
      )}

      {/* ══════════ AIRCALL MODAL ══════════ */}
      {aircallModalOpen && (
        <AircallModal
          aircallState={aircallState}
          aircallError={aircallError}
          clientName={clientName}
          phoneNumber={String(contact?.['Phone (main)'] || contact?.Phone || contact?.phone || '')}
          handleConfirmCall={handleConfirmCall}
          onClose={() => setAircallModalOpen(false)}
        />
      )}

      {/* ══════════ REASSIGN CONTACT MODAL ══════════ */}
      {reassignModalOpen && (
        <ReassignModal
          caseRef={caseRef || ''}
          reassignSearch={reassignSearch}
          handleReassignSearch={handleReassignSearch}
          reassignResults={reassignResults}
          reassignSelected={reassignSelected}
          setReassignSelected={setReassignSelected}
          handleReassignSave={handleReassignSave}
          isPending={reassignCase.isPending}
          onClose={() => {
            setReassignModalOpen(false);
            setReassignSelected(null);
            setReassignSearch('');
            setReassignResults([]);
          }}
        />
      )}

      {/* ══════════ VIEW AUDIT MODAL ══════════ */}
      {showAuditModal && (
        <CaseAuditModal
          caseRef={caseRef || ''}
          entries={auditEntries}
          isLoading={auditLoading}
          onClose={() => setShowAuditModal(false)}
          onLoadMore={loadMoreAudit}
          hasMore={hasMoreAudit}
        />
      )}
    </div>
  );
}
